import React, { useCallback, useMemo } from 'react';

import { useSnackbar } from 'notistack';
import { Link } from '~/lib/react-router-dom';

import { Box, Button, Chip, Stack } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import useCruiseDealsCrawler from '~/components/Cruises/context/useCruiseDealsCrawler';

import { formatDateShort } from '~/services/TimeService';
import DealCrawlerService, { CruiseDealCrawler } from '~/services/cruises/DealCrawlerService';

export default function DealCrawlerList() {
  const { dealCrawlers, page, total, recordsPerPage, paginate, isLoading, getDealCrawlers } = useCruiseDealsCrawler();
  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();

  const handleDelete = useCallback(
    async (id: string) => {
      const confirmed = await showConfirmDialog({
        title: 'Delete Deal Crawler?',
        description: `This will delete the deal crawler, are you sure you want to proceed?`,
      });

      if (!confirmed) {
        return;
      }

      await DealCrawlerService.deleteById(id);
      enqueueSnackbar('Deal crawler deleted successfully', { variant: 'success' });
      getDealCrawlers();
    },
    [enqueueSnackbar, getDealCrawlers, showConfirmDialog],
  );

  const columns: Array<GridColDef> = useMemo(() => {
    return [
      {
        field: 'sourceUrl',
        headerName: 'Source URL',
        sortable: true,
        disableColumnMenu: true,
        flex: 2,
        display: 'flex',
        renderCell: ({ row }) => {
          const url = row.sourceUrl;
          const label = url.replace(/\/+$/, '').split('/').pop();
          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {label}
            </a>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        sortable: false,
        disableColumnMenu: true,
        flex: 2,
        renderCell: (params) => {
          const pendingCount = params.row.sailingsByStatus.PENDING;
          const completedCount = params.row.sailingsByStatus.COMPLETED;
          const failedCount = params.row.sailingsByStatus.FAILED;

          return (
            <Stack direction="row" spacing={1}>
              {pendingCount > 0 && <Chip label={`Pending: ${pendingCount}`} color="warning" size="small" />}
              {completedCount > 0 && <Chip label={`Completed: ${completedCount}`} color="success" size="small" />}
              {failedCount > 0 && <Chip label={`Failed: ${failedCount}`} color="error" size="small" />}
            </Stack>
          );
        },
        display: 'flex',
      },
      {
        field: 'vendor.name',
        headerName: 'Vendor',
        sortable: false,
        disableColumnMenu: true,
        flex: 2,
        renderCell: ({ row }) => {
          return row.vendor.name;
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        sortable: true,
        disableColumnMenu: true,
        flex: 2,
        renderCell: (params) => formatDateShort(params.value),
        display: 'flex',
      },
      {
        field: 'action',
        headerName: 'Action',
        width: 230,
        sortable: false,
        renderCell: (params) => {
          return (
            <Box>
              <Button variant="text" size="small" component={Link} to={`/cruises/deal-crawler/${params.row.id}/view`}>
                View
              </Button>
              <Button variant="text" size="small" onClick={() => handleDelete(params.row.id)} color="error">
                Delete
              </Button>
            </Box>
          );
        },
        display: 'flex',
      },
    ];
  }, []);

  return (
    <Box mt={1}>
      <DataGrid
        loading={isLoading}
        rows={dealCrawlers || []}
        columns={columns}
        pagination
        paginationModel={{ page: page - 1, pageSize: recordsPerPage }}
        pageSizeOptions={[10]}
        getRowId={(row: CruiseDealCrawler) => row.id}
        paginationMode="server"
        rowCount={total}
        onPaginationModelChange={({ page }) => paginate(page + 1)}
        slots={{ pagination: GridPagination }}
        autoHeight
      />
    </Box>
  );
}
